/**
 * TavernPanel — Space Tavern crew hall. Two ribbons:
 *   • Crew Recruitment — pick Miner Drone Controller or Fighter Pilot (FTP locked
 *     until Mk IV), recruit rarity-rolled crew (portraits kept), assign the MDC.
 *   • Upgrade — raise the tier of any recruited crew member (bigger buff).
 * Fighter pilots are assigned to fighters over in Fleet Management.
 */
import { useState, type ReactNode } from "react";
import type { HudSnapshot, CrewCard } from "@/engine/GameState";
import { RARITY_META } from "@/game/mods/modifications";

const BASE = import.meta.env.BASE_URL;

interface Props {
  snap: HudSnapshot;
  onRecruit: (type: "mdc" | "ftp" | "rdc" | "gc" | "fdc") => void;
  onUpgradeCrew: (id: string) => void;
  onRerollCrew: (id: string) => void;
  onEquipMdc: (id: string) => void;
  onUnequipMdc: () => void;
  onEquipRdc: (id: string) => void;
  onUnequipRdc: () => void;
  onEquipGc: (id: string) => void;
  onUnequipGc: () => void;
  onEquipFdc: (id: string) => void;
  onUnequipFdc: () => void;
  onClose: () => void;
}

export default function TavernPanel({
  snap,
  onRecruit,
  onUpgradeCrew,
  onRerollCrew,
  onEquipMdc,
  onUnequipMdc,
  onEquipRdc,
  onUnequipRdc,
  onEquipGc,
  onUnequipGc,
  onEquipFdc,
  onUnequipFdc,
  onClose,
}: Props) {
  const [tab, setTab] = useState<"recruit" | "upgrade">("recruit");
  const [crewType, setCrewType] = useState<"mdc" | "ftp" | "rdc" | "gc" | "fdc">(
    "mdc",
  );
  const crew = snap.crew;
  const locked =
    (crewType === "ftp" && !crew.ftpUnlocked) ||
    (crewType === "gc" && !crew.gcUnlocked) ||
    (crewType === "fdc" && !crew.fdcUnlocked);
  const type = locked ? "mdc" : crewType;
  const roster = crew.roster.filter((c) => c.type === type);

  const card = (c: CrewCard, actions: ReactNode): ReactNode => (
    <div key={c.id} className={`crew-card rarity-${c.rarity}`}>
      <img className="crew-portrait" src={`${BASE}portraits/${c.portrait}.png`} alt="" />
      <div className="crew-info">
        <div className="crew-name">
          {c.name}
          {c.kia && <span className="crew-kia"> · ☠ K.I.A.</span>}
        </div>
        <div className="crew-sub" style={{ color: RARITY_META[c.rarity].color }}>
          {RARITY_META[c.rarity].label} · Tier {c.tier}
          {(crew.equippedMdcId === c.id ||
            crew.equippedRdcId === c.id ||
            crew.equippedGcId === c.id ||
            crew.equippedFdcId === c.id) && (
            <span className="crew-active"> · ACTIVE</span>
          )}
        </div>
        <div className="crew-buff">
          {c.kia ? "Killed in action — reroll to bring in a fresh pilot." : c.buff}
        </div>
        {!c.kia && actions}
        <button
          className={`hud-btn ${c.kia ? "accent" : "ghost"} crew-reroll`}
          disabled={snap.units < crew.rerollCost}
          onClick={() => onRerollCrew(c.id)}
          title="Re-roll rarity, portrait & name (tier resets to 1)"
        >
          🎲 {c.kia ? "Reroll (revive)" : "Reroll"} · {crew.rerollCost} U
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Space Tavern"
      >
        <div className="upg-head">
          <div className="upg-head-title">🍺 Space Tavern · ◆ {Math.floor(snap.units)} U</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="arsenal-ribbons">
          <button
            className={`arsenal-ribbon${tab === "recruit" ? " on" : ""}`}
            onClick={() => setTab("recruit")}
          >
            Crew Recruitment
          </button>
          <button
            className={`arsenal-ribbon${tab === "upgrade" ? " on" : ""}`}
            onClick={() => setTab("upgrade")}
          >
            Upgrade
          </button>
        </div>

        <div className="stats-body">
          {tab === "recruit" && (
            <>
              <div className="tavern-typebar">
                <button
                  className={`tavern-type${type === "mdc" ? " on" : ""}`}
                  onClick={() => setCrewType("mdc")}
                >
                  ⛏ Miner Drone Controller
                </button>
                <button
                  className={`tavern-type${type === "rdc" ? " on" : ""}`}
                  onClick={() => setCrewType("rdc")}
                >
                  🔧 Repair Drone Controller
                </button>
                <button
                  className={`tavern-type${type === "ftp" ? " on" : ""}`}
                  disabled={!crew.ftpUnlocked}
                  onClick={() => crew.ftpUnlocked && setCrewType("ftp")}
                >
                  {crew.ftpUnlocked ? "🚀 Fighter Pilot" : "🔒 Fighter Pilot (Mk IV)"}
                </button>
                {crew.gcUnlocked && (
                  <button
                    className={`tavern-type${type === "gc" ? " on" : ""}`}
                    onClick={() => setCrewType("gc")}
                  >
                    🎯 Gun Commander
                  </button>
                )}
                {crew.fdcUnlocked && (
                  <button
                    className={`tavern-type${type === "fdc" ? " on" : ""}`}
                    onClick={() => setCrewType("fdc")}
                  >
                    🛸 Fighter Drone Controller
                  </button>
                )}
              </div>
              <button
                className="hud-btn accent"
                disabled={snap.units < crew.recruitCost}
                onClick={() => onRecruit(type)}
              >
                Recruit{" "}
                {type === "ftp" ? "Pilot" : type === "gc" ? "Commander" : "Controller"} ·{" "}
                {crew.recruitCost} U
              </button>
              <div className="crew-grid">
                {roster.length === 0 ? (
                  <div className="arsenal-hint">
                    No {type === "ftp" ? "pilots" : "controllers"} recruited yet.
                  </div>
                ) : (
                  roster.map((c) =>
                    card(
                      c,
                      type === "mdc" ? (
                        crew.equippedMdcId === c.id ? (
                          <button className="hud-btn ghost" onClick={onUnequipMdc}>
                            Unassign
                          </button>
                        ) : (
                          <button
                            className="hud-btn accent"
                            onClick={() => onEquipMdc(c.id)}
                          >
                            Assign to drones
                          </button>
                        )
                      ) : type === "rdc" ? (
                        crew.equippedRdcId === c.id ? (
                          <button className="hud-btn ghost" onClick={onUnequipRdc}>
                            Unassign
                          </button>
                        ) : (
                          <button
                            className="hud-btn accent"
                            onClick={() => onEquipRdc(c.id)}
                          >
                            Assign to repair drones
                          </button>
                        )
                      ) : type === "gc" ? (
                        crew.equippedGcId === c.id ? (
                          <button className="hud-btn ghost" onClick={onUnequipGc}>
                            Unassign
                          </button>
                        ) : (
                          <button
                            className="hud-btn accent"
                            onClick={() => onEquipGc(c.id)}
                          >
                            Assign to guns
                          </button>
                        )
                      ) : type === "fdc" ? (
                        crew.equippedFdcId === c.id ? (
                          <button className="hud-btn ghost" onClick={onUnequipFdc}>
                            Unassign
                          </button>
                        ) : (
                          <button
                            className="hud-btn accent"
                            onClick={() => onEquipFdc(c.id)}
                          >
                            Assign to drones
                          </button>
                        )
                      ) : (
                        <div className="crew-hint">Assign in Fleet Management</div>
                      ),
                    ),
                  )
                )}
              </div>
              <div className="arsenal-hint">
                {type === "mdc"
                  ? "The assigned controller boosts your mining speed & drone capacity."
                  : type === "rdc"
                    ? "The assigned controller boosts your repair drones' healing rate."
                    : type === "gc"
                      ? "The assigned Gun Commander boosts your turrets' & cannons' damage, turn rate and fire rate."
                      : type === "fdc"
                        ? "The assigned controller boosts your fighter drones' damage, speed and HP."
                        : "Pilots fly your escort fighters — a fighter with no pilot can't launch."}
              </div>
            </>
          )}

          {tab === "upgrade" && (
            <div className="crew-grid">
              {crew.roster.length === 0 ? (
                <div className="arsenal-hint">Recruit crew first.</div>
              ) : (
                crew.roster.map((c) =>
                  card(
                    c,
                    c.canUpgrade ? (
                      <button
                        className="hud-btn accent"
                        disabled={snap.units < c.upgradeCost}
                        onClick={() => onUpgradeCrew(c.id)}
                      >
                        Upgrade → Tier {c.tier + 1} · {c.upgradeCost} U
                      </button>
                    ) : (
                      <div className="crew-hint">Max tier</div>
                    ),
                  ),
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
